
import { AIAssistantHeader } from "./AIAssistantHeader";
import { AIAssistantTabs } from "./AIAssistantTabs";
import { useAIAssistantMessages } from "./useAIAssistantMessages";
import { CalendarEvent } from "@/types/calendar";
import { useOrchestration } from "@/hooks/useOrchestration";
import { useTokenTracking } from "@/hooks/useTokenTracking";
import { useState, useEffect } from "react";
import { AIAssistantIntro } from "./AIAssistantIntro";

interface AIAssistantProps {
  event?: CalendarEvent;
}

const AIAssistant = ({ event }: AIAssistantProps) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [initError, setInitError] = useState<Error | null>(null);

  const {
    activeTab,
    setActiveTab,
    messages,
    isProcessing,
    handleSendMessage,
    onActionClick,
  } = useAIAssistantMessages(event);

  const { orchestrationActive, orchestrationInsights, availableAgents, taskComplexity } = useOrchestration();
  const { totalUsage } = useTokenTracking();

  // Set initialized after initial render
  useEffect(() => {
    try {
      // Add a small delay to ensure all hooks have loaded properly
      const timer = setTimeout(() => {
        setIsInitialized(true);
      }, 500);
      
      return () => clearTimeout(timer);
    } catch (error) {
      console.error("Error initializing AI Assistant:", error);
      setInitError(error instanceof Error ? error : new Error("Unknown error"));
    }
  }, []);

  // If we're still initializing, show a simple loading state
  if (!isInitialized && !initError) {
    return (
      <div className="flex flex-col h-full w-full bg-background items-center justify-center p-6">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
        <p className="text-sm text-muted-foreground mt-2">Loading assistant...</p>
      </div>
    );
  }

  // If there was an error initializing, show error state
  if (initError) {
    return (
      <div className="flex flex-col h-full w-full bg-background items-center justify-center p-6 text-center">
        <p className="text-red-500 mb-2">Unable to initialize assistant</p>
        <p className="text-sm text-muted-foreground">
          Please try refreshing the page or check your connection.
        </p>
        <pre className="text-xs text-red-400 mt-4 max-w-md overflow-auto p-2 bg-red-50 dark:bg-red-900/20 rounded">
          {initError.message}
        </pre>
      </div>
    );
  }

  // Convert agents to proper AvailableAgent format
  const convertedAgents = availableAgents.map(agent => ({
    id: agent.id,
    name: agent.name,
    description: agent.description,
    capabilities: agent.capabilities,
    isAvailable: agent.isAvailable,
    status: 'idle' as const // Add required status property
  }));

  return (
    <div className="flex flex-col h-full w-full bg-background border border-border rounded-lg overflow-hidden">
      <AIAssistantHeader
        orchestrationActive={orchestrationActive}
        orchestrationInsights={orchestrationInsights}
        availableAgents={convertedAgents}
        taskComplexity={taskComplexity}
        totalTokens={totalUsage.totalTokens}
        cost={totalUsage.cost}
        className="shrink-0"
      />
      <div className="flex-1 overflow-hidden flex flex-col">
        <AIAssistantTabs
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          chatProps={{
            messages,
            onSendMessage: handleSendMessage,
            isProcessing: isProcessing || orchestrationActive,
            event,
          }}
          event={event}
        />
      </div>
    </div>
  );
};

export default AIAssistant;
